import * as XLSX from "xlsx";

export const OFFICE_EXTENSIONS = [
  "doc",
  "docx",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
];

export type ExcelSheetPreview = {
  name: string;
  rows: string[][];
};

export type ExcelWorkbookPreview = {
  sheets: ExcelSheetPreview[];
};

export type DocumentViewerLoadResult = {
  viewerUrl: string | null;
  excelWorkbook: ExcelWorkbookPreview | null;
  isBlobUrl: boolean;
};

export const getFileExtension = (nameOrUrl: string): string => {
  const path = nameOrUrl.split("?")[0];
  return path.split(".").pop()?.toLowerCase() || "";
};

export const isPdfExtension = (extension: string): boolean =>
  extension === "pdf";

export const isOfficeExtension = (extension: string): boolean =>
  OFFICE_EXTENSIONS.includes(extension);

export const getOfficeViewerUrl = (blobAddress: string): string =>
  `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(blobAddress)}`;

const formatCellValue = (cell: XLSX.CellObject): string => {
  if (cell.w != null) {
    return String(cell.w);
  }
  if (cell.v == null) {
    return "";
  }
  if (cell.t === "d" && cell.v instanceof Date) {
    return cell.v.toLocaleDateString();
  }
  return String(cell.v);
};

export const parseExcelWorkbook = (buffer: ArrayBuffer): ExcelWorkbookPreview => {
  const workbook = XLSX.read(buffer, { type: "array", cellDates: true });

  const sheets = workbook.SheetNames.map((name) => {
    const sheet = workbook.Sheets[name];
    const range = sheet["!ref"]
      ? XLSX.utils.decode_range(sheet["!ref"])
      : { s: { r: 0, c: 0 }, e: { r: 0, c: 0 } };

    const rows: string[][] = [];
    for (let rowIndex = range.s.r; rowIndex <= range.e.r; rowIndex += 1) {
      const row: string[] = [];
      for (let columnIndex = range.s.c; columnIndex <= range.e.c; columnIndex += 1) {
        const cell =
          sheet[XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex })];
        row.push(cell ? formatCellValue(cell) : "");
      }
      rows.push(row);
    }

    return { name, rows };
  });

  return { sheets };
};

const fetchBlobUrl = async (
  blobAddress: string,
  contentType: string,
): Promise<string> => {
  const response = await fetch(blobAddress, {
    method: "GET",
    mode: "cors",
    credentials: "omit",
    cache: "no-cache",
  });

  if (!response.ok) {
    throw new Error(
      `Failed to download file: ${response.status} ${response.statusText}`,
    );
  }

  const downloadedBlob = await response.blob();
  const cleanBlob = new Blob([downloadedBlob], { type: contentType });
  return URL.createObjectURL(cleanBlob);
};

export const loadRemoteDocumentForViewer = async (
  blobAddress: string,
  fileName?: string,
): Promise<DocumentViewerLoadResult> => {
  const extension = getFileExtension(fileName || blobAddress);

  if (isPdfExtension(extension)) {
    return {
      viewerUrl: await fetchBlobUrl(blobAddress, "application/pdf"),
      excelWorkbook: null,
      isBlobUrl: true,
    };
  }

  if (isOfficeExtension(extension)) {
    return {
      viewerUrl: getOfficeViewerUrl(blobAddress),
      excelWorkbook: null,
      isBlobUrl: false,
    };
  }

  const response = await fetch(blobAddress, {
    method: "GET",
    mode: "cors",
    credentials: "omit",
    cache: "no-cache",
  });

  if (!response.ok) {
    throw new Error(
      `Failed to download file: ${response.status} ${response.statusText}`,
    );
  }

  const contentType =
    response.headers.get("content-type")?.split(";")[0].trim() ||
    "application/octet-stream";
  const downloadedBlob = await response.blob();
  const cleanBlob = new Blob([downloadedBlob], { type: contentType });

  return {
    viewerUrl: URL.createObjectURL(cleanBlob),
    excelWorkbook: null,
    isBlobUrl: true,
  };
};

const buildExcelWorkbookPreview = (file: File): Promise<ExcelWorkbookPreview> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const buffer = event.target?.result as ArrayBuffer;
        resolve(parseExcelWorkbook(buffer));
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = () => reject(new Error("Failed to read Excel file."));
    reader.readAsArrayBuffer(file);
  });

export const loadLocalFileForViewer = async (
  file: File,
): Promise<DocumentViewerLoadResult> => {
  const extension = getFileExtension(file.name);

  if (isPdfExtension(extension)) {
    return {
      viewerUrl: URL.createObjectURL(file),
      excelWorkbook: null,
      isBlobUrl: true,
    };
  }

  if (extension === "xls" || extension === "xlsx") {
    return {
      viewerUrl: null,
      excelWorkbook: await buildExcelWorkbookPreview(file),
      isBlobUrl: false,
    };
  }

  if (isOfficeExtension(extension)) {
    throw new Error(
      "In-browser preview is only available for Excel files before upload. Please download the file to view it.",
    );
  }

  return {
    viewerUrl: URL.createObjectURL(file),
    excelWorkbook: null,
    isBlobUrl: true,
  };
};
